import { useState } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface Problem {
  name: string;
  severity: 'low' | 'moderate' | 'high';
  description: string;
  solution: string;
}

interface DiagnosticResultProps {
  healthScore: number;
  problems: Problem[];
  recommendations: string;
  imageUrl: string;
  createdAt?: string;
}

export function DiagnosticResult({ 
  healthScore, 
  problems, 
  recommendations, 
  imageUrl,
  createdAt 
}: DiagnosticResultProps) {
  const [expandedProblem, setExpandedProblem] = useState<number | null>(null);

  const getHealthColor = (score: number) => {
    if (score >= 70) return 'text-green-600 bg-green-100';
    if (score >= 40) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getHealthLabel = (score: number) => {
    if (score >= 70) return 'Saudável';
    if (score >= 40) return 'Atenção';
    return 'Crítico';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'bg-yellow-100 text-yellow-800';
      case 'moderate': return 'bg-orange-100 text-orange-800';
      case 'high': return 'bg-red-100 text-red-800';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'low': return 'Leve';
      case 'moderate': return 'Moderado';
      case 'high': return 'Grave';
      default: return severity;
    }
  };

  return (
    <div className="space-y-4">
      {/* Health Score Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <img 
              src={imageUrl} 
              alt="Diagnóstico" 
              className="w-20 h-20 rounded-lg object-cover"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className={`text-3xl font-bold ${getHealthColor(healthScore).split(' ')[0]}`}>
                  {healthScore}%
                </div>
                <Badge className={getHealthColor(healthScore)}>
                  {getHealthLabel(healthScore)}
                </Badge>
              </div>
              {createdAt && (
                <p className="text-sm text-muted-foreground">
                  {new Date(createdAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Problems List */}
      {problems.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              Problemas Identificados ({problems.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {problems.map((problem, index) => (
              <div 
                key={index}
                className="border rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setExpandedProblem(expandedProblem === index ? null : index)}
                  className="w-full p-3 flex items-center justify-between bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-destructive" />
                    <span className="font-medium text-sm">{problem.name}</span>
                    <Badge variant="secondary" className={getSeverityColor(problem.severity)}>
                      {getSeverityLabel(problem.severity)}
                    </Badge>
                  </div>
                  {expandedProblem === index ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
                
                {expandedProblem === index && (
                  <div className="p-3 space-y-3 bg-background">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Descrição</p>
                      <p className="text-sm">{problem.description}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Solução</p>
                      <p className="text-sm">{problem.solution}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* No Problems */}
      {problems.length === 0 && healthScore >= 70 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">Nenhum problema identificado!</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Sua planta parece estar saudável. Continue com os cuidados atuais.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recomendações</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {recommendations}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
